import { z } from "zod";
export const BI_REFERENCE_SCHEMA = "wetalk-bi-graph-v1";
export const BI_CONFIGURATION_FIELD = "bi_configuration";
export const BI_LIMIT = Object.freeze({ sources: 10, datasets: 256, schemaColumns: 512, configurationChars: 262144, configurationBytes: 400000, rows: 2000, columns: 64, cell: 4000, bytes: 2_000_000, results: 100 });
const name = z.string().min(1).max(120).refine((value) => !["__proto__", "constructor", "prototype"].includes(value), "Reserved name");
export const BiScalarSchema = z.union([z.string().max(BI_LIMIT.cell), z.number().finite().refine((value) => !Number.isInteger(value) || Number.isSafeInteger(value), "Unsafe integers must be represented as text"), z.boolean(), z.null()]);
export const BiEndpointSchema = z.object({
    id: name,
    url: z.string().max(2048).refine((value) => {
        try {
            const url = new URL(value);
            return url.protocol === "https:" && url.username === "" && url.password === "" && url.search === "" && url.hash === "" && (url.port === "" || url.port === "443");
        }
        catch {
            return false;
        }
    }, "Use a public HTTPS URL without credentials, query parameters or fragments"),
    recordsPath: z.string().max(240).default(""),
    excludedColumns: z.array(name).max(BI_LIMIT.schemaColumns).optional(),
    availableColumns: z.array(name).max(BI_LIMIT.schemaColumns).optional(),
}).strict();
export const BiRelationshipSchema = z.object({ id: name, from: name, fromColumn: name, to: name, toColumn: name }).strict();
// eslint-disable-next-line no-control-regex -- reject control characters in external database identifiers
const identifier = name.refine((value) => !/[\u0000-\u001f]/u.test(value), "Use a valid database identifier");
const columnIdentifier = identifier.refine((value) => !value.includes("."), "Use a top-level column name without dots");
const BiDatabaseSourceBaseSchema = z.object({
    id: name,
    kind: z.enum(["postgres", "mysql", "mssql", "mongodb", "snowflake", "clickhouse", "elasticsearch"]),
    host: z.string().min(1).max(253).regex(/^[a-zA-Z0-9.-]+$/u, "Use a host name without a scheme, path or credentials"),
    port: z.number().int().min(1).max(65535).optional(),
    mysqlTls: z.boolean().optional(),
    database: identifier,
    table: z.string().min(1).max(240),
    columns: z.array(columnIdentifier).min(1).max(BI_LIMIT.schemaColumns).refine((value) => new Set(value).size === value.length, "Columns repeat"),
    secretAlias: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/u, "Use the alias of an account-scoped vault credential"),
    warehouse: identifier.optional(),
    authDatabase: identifier.optional(),
}).strict();
export const BiDatabaseSourceSchema = BiDatabaseSourceBaseSchema.refine((source) => 
// eslint-disable-next-line no-control-regex -- reject control characters in collection and index names
source.kind === "elasticsearch" || source.kind === "mongodb" ? !/[\u0000-\u001f]/u.test(source.table)
    : /^[\p{L}\p{N}_ -]+(?:\.[\p{L}\p{N}_ -]+)?$/u.test(source.table), { message: "Use a valid table, collection or index name", path: ["table"] });
export const BiTableSelectionSchema = z.object({
    id: name, table: z.string().min(1).max(240), columns: z.array(name).min(1).max(BI_LIMIT.schemaColumns),
    excluded: z.boolean().default(false), excludedColumns: z.array(name).max(BI_LIMIT.schemaColumns).default([]),
}).strict();
export const BiDatabaseConnectionSchema = BiDatabaseSourceBaseSchema.omit({ table: true, columns: true }).extend({
    tables: z.array(BiTableSelectionSchema).min(1).max(BI_LIMIT.datasets),
    discoveryNote: z.string().max(400).optional(),
}).strict();
export const BiDatabaseDiscoverySchema = BiDatabaseSourceBaseSchema.omit({ table: true, columns: true, secretAlias: true });
export function biSelectedDatasets(source) {
    const { tables, discoveryNote: _note, ...connection } = source;
    return tables.filter((table) => !table.excluded).map((table) => BiDatabaseSourceSchema.parse({ ...connection, id: table.id, table: table.table, columns: table.columns.filter((column) => !table.excludedColumns.includes(column)) }));
}
export function biDatasetChoices(sources) {
    return sources.flatMap((source) => "tables" in source ? source.tables.filter((table) => !table.excluded).map((table) => ({ id: table.id, label: `${source.id} / ${table.table}`, columns: table.columns.filter((column) => !table.excludedColumns.includes(column)) })) : [{ id: source.id, label: source.id, columns: "columns" in source ? source.columns : [] }]);
}
export const BiConnectorSourceSchema = z.union([BiEndpointSchema, BiDatabaseSourceSchema, BiDatabaseConnectionSchema]);
/** Secret-bearing input only; never a field-value or published configuration. */
export const BiConnectionRequestSchema = z.object({
    source: z.union([BiDatabaseSourceBaseSchema.omit({ secretAlias: true }), BiDatabaseDiscoverySchema, BiEndpointSchema]),
    authentication: z.object({ username: z.string().min(1).max(256).optional(), password: z.string().max(4096).optional(), token: z.string().min(1).max(16000).optional() }).strict().optional(),
}).strict();
export const BiConfigurationSchema = z.object({
    sources: z.array(BiConnectorSourceSchema).max(BI_LIMIT.sources).default([]),
    relationships: z.array(BiRelationshipSchema).max(20).default([]),
    fileExcludedColumns: z.record(name, z.array(name).max(BI_LIMIT.schemaColumns)).default({}),
}).strict().refine((value) => JSON.stringify(value).length <= BI_LIMIT.configurationChars && new TextEncoder().encode(JSON.stringify(value)).byteLength <= BI_LIMIT.configurationBytes, "The selected database schemas are too large to save. Reduce accessible tables or columns.");
export function biConfigurationOf(text) {
    return BiConfigurationSchema.parse(text.trim() === "" ? {} : JSON.parse(text));
}
export const BiSourceSchema = z.object({
    id: name, kind: z.enum(["csv", "json", "https_json", "postgres", "mysql", "mssql", "mongodb", "snowflake", "clickhouse", "elasticsearch"]), origin: z.string().min(1).max(2048),
    sha256: z.string().regex(/^[0-9a-f]{64}$/u), readAt: z.string().datetime(),
    columns: z.array(name).min(1).max(BI_LIMIT.columns),
    rows: z.array(z.record(z.string(), BiScalarSchema)).max(BI_LIMIT.rows),
}).strict();
export const BiEdgeSchema = z.object({ relationship: name, fromSource: name, fromRow: z.number().int().nonnegative(), toSource: name, toRow: z.number().int().nonnegative() }).strict();
export const BiGraphSchema = z.object({
    schema: z.literal(BI_REFERENCE_SCHEMA), builtAt: z.string().datetime(), digest: z.string().regex(/^[0-9a-f]{64}$/u),
    sources: z.array(BiSourceSchema).min(1).max(BI_LIMIT.datasets),
    relationships: z.array(BiRelationshipSchema).max(20), edges: z.array(BiEdgeSchema).max(BI_LIMIT.rows * 20),
    warnings: z.array(z.string().max(400)).max(20),
}).strict().superRefine((graph, context) => {
    const invalid = (message) => context.addIssue({ code: "custom", message });
    const sources = new Map(graph.sources.map((source) => [source.id, source]));
    if (sources.size !== graph.sources.length || graph.sources.reduce((sum, source) => sum + source.rows.length, 0) > BI_LIMIT.rows)
        invalid("Invalid source identities or total record count");
    for (const source of graph.sources) {
        if (new Set(source.columns).size !== source.columns.length || source.columns.some((column) => column.includes(".")) || source.rows.some((row) => Object.keys(row).length !== source.columns.length || source.columns.some((column) => !Object.hasOwn(row, column))))
            invalid("Invalid or inconsistent source columns");
    }
    const relations = new Map(graph.relationships.map((relation) => [relation.id, relation]));
    if (relations.size !== graph.relationships.length)
        invalid("Duplicate relationships");
    const expected = new Set();
    const keyOf = (value) => JSON.stringify([typeof value, value]);
    for (const relation of graph.relationships) {
        const from = sources.get(relation.from);
        const to = sources.get(relation.to);
        if (from === undefined || to === undefined || !from.columns.includes(relation.fromColumn) || !to.columns.includes(relation.toColumn)) {
            invalid("Unknown relationship dataset or column");
            continue;
        }
        const parent = new Map();
        for (const [index, row] of to.rows.entries()) {
            const value = row[relation.toColumn];
            if (value === null)
                continue;
            const key = keyOf(value);
            if (parent.has(key))
                invalid("Ambiguous parent key");
            parent.set(key, index);
        }
        for (const [index, row] of from.rows.entries()) {
            const value = row[relation.fromColumn];
            if (value === null)
                continue;
            const target = parent.get(keyOf(value));
            if (target !== undefined)
                expected.add(JSON.stringify([relation.id, from.id, index, to.id, target]));
        }
    }
    const actual = new Set(graph.edges.map((edge) => JSON.stringify([edge.relationship, edge.fromSource, edge.fromRow, edge.toSource, edge.toRow])));
    if (actual.size !== graph.edges.length || actual.size !== expected.size || [...actual].some((edge) => !expected.has(edge)))
        invalid("Graph edges disagree with the declared foreign keys");
    if (new TextEncoder().encode(JSON.stringify(graph)).byteLength > BI_LIMIT.bytes)
        invalid("Graph exceeds the snapshot byte limit");
});
export function biGraphOf(value) {
    const parsed = BiGraphSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
export const BiQuerySchema = z.object({
    source: name,
    relationship: name.optional(),
    filters: z.array(z.object({ column: name, operator: z.enum(["eq", "ne", "gt", "gte", "lt", "lte", "contains"]), value: BiScalarSchema }).strict()).max(10).default([]),
    groupBy: z.array(name).max(3).default([]),
    metric: z.enum(["rows", "count", "sum", "average", "min", "max"]),
    column: name.optional(),
    columns: z.array(name).max(10).optional(),
    limit: z.number().int().min(1).max(BI_LIMIT.results).default(20),
}).strict();
export const BI_QUERY_TOOL_NAME = "bi_query";
/** JSON Schema is also used by the runtime; the executor validates again with BiQuerySchema. */
export const BI_QUERY_TOOL = Object.freeze({
    operationId: BI_QUERY_TOOL_NAME,
    description: "Query the version's BI knowledge graph. Use a source ID from DATA SCHEMA. Base columns use their names; joined parent columns use relationshipId.column. One many-to-one relationship is allowed. Filters are ANDed. Aggregates cover all matching base records, never just the returned limit. Cite returned source references and snapshot date. No SQL or network access.",
    parameterSchema: z.toJSONSchema(BiQuerySchema),
});
//# sourceMappingURL=schema.js.map